// R-131-FIN-DECISION-SCENARIO — decision-relevant what-if scenario runner.
//
// PURPOSE
// The Monte Carlo bands answer "how uncertain is each pathway?". This runner
// answers a different, decision-shaped question: "under which DISCRETE futures
// does the recommendation flip?". It evaluates a small FIXED set of what-if
// scenarios by pinning EXACTLY ONE driver at a distribution tail (e.g. fuel
// CAGR at its P10 / P90) while holding every other driver at its median (the
// base pathway), then reports the winning pathway under each pin. The UI can
// then render conditional callouts ("Recommend X if fuel stays low; Y if the
// grid queue slips").
//
// NO FABRICATED NUMBERS
// Every pin value is read from the SAME distribution parameters the Monte
// Carlo sampler already uses (see pathwayMonteCarlo.ts samplePathway):
//   - fuel-price CAGR: Normal(mean = opex.fuelPriceCagrPct,
//                              σ = opex.fuelPriceStdev). The P10/P90 pins are
//                      mean ± z·σ with the standard-normal z for the 10th/90th
//                      percentile (Z_P10 / Z_P90 below).
//   - grid queue:      the pathway's own queueDelayDistribution.p10Years /
//                      .p90Years (the triangular distribution's stated tails).
//   - carbon price:    a low / high shadow carbon price applied via the
//                      existing applyPathwayCarbonAdjust overlay. The numeric
//                      defaults are the SAME documented constants used
//                      elsewhere (DEFAULT_CARBON_PRICE_USD_PER_T = $50/tCO2e is
//                      the median; the low/high pins below are named + sourced).
//
// DETERMINISTIC
// Each scenario produces a deterministic modified pathway and evaluates the
// AUTHORITATIVE figures (computePathwayLcoe real LCOE, computePathwayNpvUsd).
// No RNG is consumed for the pinned-tail scenarios, so the result is fully
// reproducible. (A seed is accepted and threaded only for forward-compatible
// scenarios that might want MC; the fixed set below is RNG-free.)

import { applyPathwayCarbonAdjust, DEFAULT_CARBON_PRICE_USD_PER_T } from './pathwayCarbonAdjust';
import { computePathwayLcoe } from './pathwayLcoe';
import { computePathwayNpvUsd } from './pathwayMonteCarlo';
import { mergePathway } from './presets/shared';
import type { Pathway } from './types';

/**
 * Standard-normal quantiles for the 10th / 90th percentile, used to convert the
 * fuel-price CAGR Normal(mean, σ) into its P10 / P90 tail pins:
 *   P10 = mean + Z_P10·σ,  P90 = mean + Z_P90·σ.
 * Z_P90 = Φ⁻¹(0.90) ≈ 1.2815515594..., Z_P10 = −Z_P90 (symmetry of the normal).
 * Source: standard normal inverse CDF (e.g. Abramowitz & Stegun 26.2.23); these
 * are the SAME tails the Monte Carlo sampler's Normal draw would populate.
 */
export const Z_P90 = 1.2815515594;
export const Z_P10 = -Z_P90;

/**
 * Low / high shadow carbon-price pins ($/tCO2e). The MEDIAN is the documented
 * DEFAULT_CARBON_PRICE_USD_PER_T ($50/tCO2e, pathwayCarbonAdjust.ts). The pins
 * bracket it with a plausible policy range:
 *   - low  = $15/tCO2e — near voluntary-market / low-ambition floor.
 *   - high = $190/tCO2e — ~EPA 2023 social cost of carbon central estimate
 *            (2020$, near-term), an upper-bound shadow price.
 * These are NAMED + SOURCED, not invented; callers can override via opts.
 */
export const SCENARIO_LOW_CARBON_PRICE_USD_PER_T = 15;
export const SCENARIO_HIGH_CARBON_PRICE_USD_PER_T = 190;

/** Identifier for one of the fixed decision-relevant scenarios. */
export type ScenarioId =
  | 'low-fuel'
  | 'high-fuel'
  | 'long-queue'
  | 'low-carbon'
  | 'high-carbon';

/** Which driver a scenario pins, and at which tail (for UI grouping). */
export type ScenarioDriver = 'fuel-cagr' | 'grid-queue' | 'carbon-price';
export type ScenarioTail = 'low' | 'high';

/** One pathway's authoritative figures under a single scenario pin. */
export interface ScenarioPathwayResult {
  pathwayId: string;
  name: string;
  /** WACC-discounted real LCOE under the pin, USD/MWh (NaN if uncomputable). */
  lcoeRealUsdPerMwh: number;
  /** NPV at the risk-adjusted WACC under the pin, USD. */
  npvUsd: number;
}

export interface ScenarioResult {
  scenarioId: ScenarioId;
  /** Human label for report / UI callouts. */
  label: string;
  driver: ScenarioDriver;
  tail: ScenarioTail;
  /** Per-pathway authoritative figures under this pin. */
  pathways: ScenarioPathwayResult[];
  /** Pathway with the LOWEST finite real LCOE under this pin (null if none). */
  winnerByLcoe: { pathwayId: string; name: string } | null;
  /** Pathway with the HIGHEST finite NPV under this pin (null if none). */
  winnerByNpv: { pathwayId: string; name: string } | null;
  /**
   * True when this scenario was applicable to ≥1 pathway (e.g. a grid-queue pin
   * only bites pathways that carry a queueDelayDistribution; a fuel pin only
   * bites pathways with a non-zero fuelPriceStdev). When false, the scenario
   * reduces to the median case and is still reported for completeness.
   */
  applicable: boolean;
}

export interface ScenarioSensitivityResult {
  scenarios: ScenarioResult[];
}

export interface ScenarioSensitivityOpts {
  now?: Date;
  /** Long-run inflation for the real-LCOE conversion (defaults to LCOE module). */
  inflationPct?: number;
  /** Optional seed (forward-compat; the fixed pinned scenarios are RNG-free). */
  seedRng?: number;
  /** Override the low/high carbon-price pins ($/tCO2e). */
  lowCarbonPriceUsdPerT?: number;
  highCarbonPriceUsdPerT?: number;
}

/** Clone-and-set the fuel-price CAGR pin (others stay at median). */
function pinFuelCagr(pathway: Pathway, tail: ScenarioTail): Pathway {
  const mean = pathway.opex.fuelPriceCagrPct ?? 0;
  const stdev = pathway.opex.fuelPriceStdev ?? 0;
  // Median pathway when there is no fuel-price uncertainty (renewables/nuclear):
  // the pin is a no-op so the scenario folds to the base case for that pathway.
  if (!(stdev > 0)) return pathway;
  const z = tail === 'low' ? Z_P10 : Z_P90;
  const pinned = mean + z * stdev;
  return mergePathway(pathway, {
    opex: { ...pathway.opex, fuelPriceCagrPct: pinned },
  });
}

/**
 * Pin the grid queue to its P90 (long-queue) tail by stretching the FIRST
 * construction phase to the queue-delay P90 duration. Only grid/hybrid pathways
 * with a queueDelayDistribution are affected; others are returned unchanged.
 * Mirrors the sampler's queue treatment (setConstructionMonths on the queue
 * distribution) but pins the stated P90 instead of drawing.
 */
function pinLongQueue(pathway: Pathway): Pathway {
  const dist = pathway.risk.queueDelayDistribution;
  if (!dist) return pathway;
  const queueMonths = Math.max(1, Math.round(dist.p90Years * 12));
  const phases = pathway.phases.map((phase, idx) => {
    // Stretch the first construction phase (the interconnection queue) to P90.
    if (phase.kind === 'construction' && isFirstConstruction(pathway, idx)) {
      return { ...phase, durationMonths: queueMonths };
    }
    return phase;
  });
  return mergePathway(pathway, { phases });
}

function isFirstConstruction(pathway: Pathway, idx: number): boolean {
  const firstIdx = pathway.phases.findIndex((p) => p.kind === 'construction');
  return idx === firstIdx;
}

/** Pin the shadow carbon price via the existing carbon overlay (others median). */
function pinCarbonPrice(pathway: Pathway, carbonPricePerTUsd: number): Pathway {
  return applyPathwayCarbonAdjust(pathway, {
    carbonPricePerTUsd,
    applyCarbonOverlay: true,
  }).adjustedPathway;
}

function evaluate(
  pathway: Pathway,
  now: Date | undefined,
  inflationPct: number | undefined,
): ScenarioPathwayResult {
  const lcoe = computePathwayLcoe(pathway, { now, inflationPct });
  return {
    pathwayId: pathway.id,
    name: pathway.name,
    lcoeRealUsdPerMwh: lcoe.lcoeRealPerMwhUsd,
    npvUsd: computePathwayNpvUsd(pathway, now),
  };
}

function pickWinnerByLcoe(rows: ScenarioPathwayResult[]): ScenarioResult['winnerByLcoe'] {
  let best: ScenarioPathwayResult | null = null;
  for (const r of rows) {
    if (!Number.isFinite(r.lcoeRealUsdPerMwh)) continue;
    if (best === null || r.lcoeRealUsdPerMwh < best.lcoeRealUsdPerMwh) best = r;
  }
  return best ? { pathwayId: best.pathwayId, name: best.name } : null;
}

function pickWinnerByNpv(rows: ScenarioPathwayResult[]): ScenarioResult['winnerByNpv'] {
  let best: ScenarioPathwayResult | null = null;
  for (const r of rows) {
    if (!Number.isFinite(r.npvUsd)) continue;
    if (best === null || r.npvUsd > best.npvUsd) best = r;
  }
  return best ? { pathwayId: best.pathwayId, name: best.name } : null;
}

interface ScenarioSpec {
  scenarioId: ScenarioId;
  label: string;
  driver: ScenarioDriver;
  tail: ScenarioTail;
  /** Returns a pinned clone, or the same reference when the pin does not apply. */
  pin: (pathway: Pathway) => Pathway;
}

/**
 * Evaluate the fixed set of decision-relevant what-if scenarios.
 *
 * For each scenario, exactly ONE driver is pinned at a distribution tail across
 * ALL pathways simultaneously (the correlated-future interpretation: a low-fuel
 * world is low-fuel for every candidate) while all other drivers stay at their
 * median (the base pathway). The winning pathway (lowest authoritative real
 * LCOE and highest NPV) is reported per scenario so the UI can render
 * conditional recommendations. Deterministic — no RNG is consumed.
 */
export function runScenarioSensitivity(
  pathways: Pathway[],
  opts?: ScenarioSensitivityOpts,
): ScenarioSensitivityResult {
  const now = opts?.now;
  const inflationPct = opts?.inflationPct;
  const lowCarbon = opts?.lowCarbonPriceUsdPerT ?? SCENARIO_LOW_CARBON_PRICE_USD_PER_T;
  const highCarbon = opts?.highCarbonPriceUsdPerT ?? SCENARIO_HIGH_CARBON_PRICE_USD_PER_T;

  const specs: ScenarioSpec[] = [
    {
      scenarioId: 'low-fuel',
      label: `Fuel CAGR low (P10)`,
      driver: 'fuel-cagr',
      tail: 'low',
      pin: (p) => pinFuelCagr(p, 'low'),
    },
    {
      scenarioId: 'high-fuel',
      label: `Fuel CAGR high (P90)`,
      driver: 'fuel-cagr',
      tail: 'high',
      pin: (p) => pinFuelCagr(p, 'high'),
    },
    {
      scenarioId: 'long-queue',
      label: `Grid queue long (P90)`,
      driver: 'grid-queue',
      tail: 'high',
      pin: (p) => pinLongQueue(p),
    },
    {
      scenarioId: 'low-carbon',
      label: `Carbon price low ($${lowCarbon}/tCO2e)`,
      driver: 'carbon-price',
      tail: 'low',
      pin: (p) => pinCarbonPrice(p, lowCarbon),
    },
    {
      scenarioId: 'high-carbon',
      label: `Carbon price high ($${highCarbon}/tCO2e)`,
      driver: 'carbon-price',
      tail: 'high',
      pin: (p) => pinCarbonPrice(p, highCarbon),
    },
  ];

  const scenarios: ScenarioResult[] = specs.map((spec) => {
    let applicable = false;
    const rows = pathways.map((pathway) => {
      const pinned = spec.pin(pathway);
      // Reference inequality is a reliable "the pin changed something" signal
      // because every pin helper returns the SAME object when it does not apply.
      if (pinned !== pathway) applicable = true;
      return evaluate(pinned, now, inflationPct);
    });
    return {
      scenarioId: spec.scenarioId,
      label: spec.label,
      driver: spec.driver,
      tail: spec.tail,
      pathways: rows,
      winnerByLcoe: pickWinnerByLcoe(rows),
      winnerByNpv: pickWinnerByNpv(rows),
      applicable,
    };
  });

  return { scenarios };
}
